import {
  GEO_DETAIL_FOLD_END,
  GEO_DETAIL_FOLD_START,
} from '../../common/geoAnswer';

export type GeoAnswerCheckId =
  | 'conclusion'
  | 'conclusion_length'
  | 'structure'
  | 'evidence'
  | 'source'
  | 'code'
  | 'verification'
  | 'freshness'
  | 'no_fake_source';

export interface GeoAnswerCheck {
  id: GeoAnswerCheckId;
  label: string;
  hint: string;
  passed: boolean;
}

export interface GeoAnswerAnalysis {
  score: number;
  checks: GeoAnswerCheck[];
  sourceUrls: string[];
  riskyClaims: string[];
  missingSourceHints: string[];
}

export interface EvidenceSource {
  label: string;
  url: string;
}

const realUrlPattern =
  /https?:\/\/(?!example\.com|example\.org|localhost)[^\s)\]}>"']+/gi;
const anyUrlPattern = /https?:\/\/[^\s)\]}>"']+/gi;
const headingPattern = /^#{2,4}\s*(.+)$/;
const codeBlockPattern = /```[\s\S]*?```|~~~[\s\S]*?~~~/;
const inlineCodePattern = /`[^`]+`/;
const placeholderSourcePattern =
  /(example\.com|example\.org|待补充|待替换|TODO|官方文档链接|真实 URL|真实URL)/i;

const conclusionPattern =
  /(结论|答案|建议|推荐|可以|不能|不建议|根因|核心原因|先说结论)/i;
const solutionPattern = /(解决方案|处理方法|做法|步骤|配置|实现|方案)/i;
const verificationPattern = /(验证|测试|复现|检查|确认|命令输出|预期结果)/i;
const freshnessPattern =
  /(版本|环境|更新时间|适用于|从.*开始|since|release|changelog|变更)/i;
const evidencePattern =
  /(来源|参考|引用|证据|依据|官方文档|规范|RFC|release|commit|issue|PR)/i;
const riskyClaimPattern =
  /(据统计|数据显示|研究表明|论文指出|性能提升|吞吐|延迟|准确率|召回率|市场份额|官方推荐|行业标准|最佳实践)/gi;
const sourceNeededPattern =
  /(版本|性能|吞吐|延迟|准确率|规范|RFC|CVE|漏洞|论文|统计|官方|release|changelog|benchmark|兼容性|许可证)/i;

const unique = (items: string[]) => Array.from(new Set(items));

const cleanUrl = (url: string) => url.replace(/[.,;:，。；：]+$/, '');

const sourceLabelFromLine = (line: string, url: string) => {
  const beforeUrl = line.slice(0, line.indexOf(url)).trim();
  const cleaned = beforeUrl
    .replace(/^[-*]\s*/, '')
    .replace(/[:：\-—–]\s*$/, '')
    .replace(/^\d+[.)、]\s*/, '')
    .trim();
  if (cleaned) {
    return cleaned;
  }
  try {
    return new URL(url).hostname;
  } catch {
    return '来源';
  }
};

export const GEO_ANSWER_TEMPLATE = `## 结论
用 64 个字以内直接给最终判断。

## 解决方案
1. 写第一步。
2. 写第二步。
3. 写如何确认已解决。
`;

export const GEO_AI_ANSWER_TEMPLATE = `## 结论
用 64 个字以内直接给最终判断。

## 解决方案
1. 写可执行步骤、命令、配置或代码。
2. 写用户下一步应该怎么做。

${GEO_DETAIL_FOLD_START}

## 原因
- 说明问题出现的条件、根因和影响范围。

## 验证方法
1. 写出复现或验证步骤。
2. 贴出关键输出、日志或预期结果。

## 注意事项
- 写明兼容性、风险、限制和不适用场景。

## 证据链
- 结论依据：[请替换为真实官方文档、源码、Issue、Release 或规范链接](https://example.com)
- 版本/性能/规范依据：[请替换为真实来源链接](https://example.com)

${GEO_DETAIL_FOLD_END}
`;

export const appendGeoAnswerTemplate = (content: string) => {
  const current = content.trim();
  if (!current) {
    return GEO_ANSWER_TEMPLATE;
  }
  return `${current}\n\n${GEO_ANSWER_TEMPLATE}`;
};

export const extractSourceUrls = (content: string) =>
  unique((content.match(realUrlPattern) || []).map(cleanUrl));

export const parseEvidenceSources = (rawSources: string) => {
  const sources: EvidenceSource[] = [];
  const seenUrls = new Set<string>();

  rawSources
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .forEach((line) => {
      const urls = line.match(realUrlPattern) || [];
      urls.forEach((rawUrl) => {
        const url = cleanUrl(rawUrl);
        if (seenUrls.has(url)) {
          return;
        }
        seenUrls.add(url);
        sources.push({
          label: sourceLabelFromLine(line, rawUrl),
          url,
        });
      });
    });

  return sources;
};

export const formatEvidenceSources = (rawSources: string) => {
  const sources = parseEvidenceSources(rawSources);
  if (sources.length === 0) {
    return '';
  }
  return sources.map((source) => `- ${source.label}: ${source.url}`).join('\n');
};

export const appendEvidenceSources = (content: string, rawSources: string) => {
  const current = content.trim();
  const existingUrls = new Set(extractSourceUrls(current));
  const sources = parseEvidenceSources(rawSources).filter(
    (source) => !existingUrls.has(source.url),
  );

  if (sources.length === 0) {
    return content;
  }

  const newLines = sources.map((source) => `- ${source.label}: ${source.url}`);
  if (!current) {
    return `## 证据链\n${newLines.join('\n')}`;
  }

  const lines = current.split(/\r?\n/);
  const evidenceIndex = lines.findIndex((line) =>
    /^##\s*(证据链|参考来源|引用来源)\s*$/.test(line.trim()),
  );
  if (evidenceIndex < 0) {
    return `${current}\n\n## 证据链\n${newLines.join('\n')}`;
  }

  const nextHeadingIndex = lines.findIndex(
    (line, index) => index > evidenceIndex && /^##\s+/.test(line.trim()),
  );
  let insertIndex = nextHeadingIndex > -1 ? nextHeadingIndex : lines.length;
  while (
    insertIndex > evidenceIndex + 1 &&
    lines[insertIndex - 1].trim() === ''
  ) {
    insertIndex -= 1;
  }
  lines.splice(insertIndex, 0, ...newLines);
  return lines.join('\n');
};

export const analyzeGeoAnswer = (content: string): GeoAnswerAnalysis => {
  const trimmed = content.trim();
  const headings = trimmed
    .split(/\r?\n/)
    .map((line) => line.match(headingPattern)?.[1] || '')
    .filter(Boolean);
  const firstParagraph = trimmed.split(/\n\s*\n/)[0] || '';
  const normalizedFirstParagraph = firstParagraph
    .replace(/^#{1,6}\s*结论\s*/m, '')
    .replace(/\*\*|__|`|^-+\s*/g, '')
    .replace(/^结论[:：]\s*/, '')
    .trim();
  const conclusionLength = Array.from(normalizedFirstParagraph).length;
  const sourceUrls = extractSourceUrls(trimmed);
  const riskyClaims = unique(trimmed.match(riskyClaimPattern) || []);
  const hasCode =
    codeBlockPattern.test(trimmed) || inlineCodePattern.test(trimmed);
  const hasSource = sourceUrls.length > 0;
  const hasPlaceholderSource = placeholderSourcePattern.test(trimmed);
  const hasSourceNeededContent = sourceNeededPattern.test(trimmed);
  const hasAnyUrl = (trimmed.match(anyUrlPattern) || []).length > 0;

  const checks: GeoAnswerCheck[] = [
    {
      id: 'conclusion',
      label: '开头先给结论',
      hint: '首段用 1 句话回答核心问题。',
      passed: conclusionPattern.test(firstParagraph),
    },
    {
      id: 'conclusion_length',
      label: '结论不超过 64 字',
      hint: '结论越短，用户和大模型越容易抓住重点。',
      passed: conclusionLength > 0 && conclusionLength <= 64,
    },
    {
      id: 'structure',
      label: '解决方案靠前',
      hint: '默认只需要“结论 / 解决方案”；原因、验证和来源可放折叠详情。',
      passed: headings.some((heading) => solutionPattern.test(heading)),
    },
    {
      id: 'evidence',
      label: '有证据链',
      hint: '把关键结论和来源链接对应起来，便于大模型引用。',
      passed: evidencePattern.test(trimmed) && hasSource,
    },
    {
      id: 'source',
      label: '真实来源链接',
      hint: '涉及事实、版本、性能、规范时，至少放 1 个真实 URL。',
      passed: hasSource && !hasPlaceholderSource,
    },
    {
      id: 'code',
      label: '技术细节可验证',
      hint: '技术回答尽量给命令、代码块、配置片段或日志输出。',
      passed: hasCode,
    },
    {
      id: 'verification',
      label: '给出验证方法',
      hint: '说明如何复现、检查、测试或确认修复有效。',
      passed: verificationPattern.test(trimmed),
    },
    {
      id: 'freshness',
      label: '版本和适用范围清晰',
      hint: '写明环境、版本、更新时间或适用范围，降低过期风险。',
      passed: freshnessPattern.test(trimmed),
    },
    {
      id: 'no_fake_source',
      label: '没有虚假引用占位',
      hint: '不要保留 example.com、待补充、TODO 等占位来源。',
      passed: !hasPlaceholderSource,
    },
  ];

  const missingSourceHints: string[] = [];
  if (hasSourceNeededContent && !hasSource) {
    missingSourceHints.push(
      '正文涉及版本、性能、规范或官方事实，但还没有真实来源链接。',
    );
  }
  if (riskyClaims.length > 0 && !hasSource) {
    missingSourceHints.push(
      '正文包含统计、研究、性能或最佳实践类表述，需要补官方文档、论文、Issue、Release 或基准报告。',
    );
  }
  if (hasAnyUrl && !hasSource) {
    missingSourceHints.push(
      '当前 URL 看起来是占位或本地地址，请替换成可公开访问的真实来源。',
    );
  }
  if (hasPlaceholderSource) {
    missingSourceHints.push(
      '模板中的示例来源还没有替换，请改成真实 URL 后再发布。',
    );
  }

  const passedCount = checks.filter((check) => check.passed).length;
  return {
    score: Math.round((passedCount / checks.length) * 100),
    checks,
    sourceUrls,
    riskyClaims,
    missingSourceHints: unique(missingSourceHints),
  };
};
